import React, { useEffect, useMemo } from 'react';
import { Form, Row, Col, Typography, TimePicker } from 'antd';
import Modal from '../../common/Modal';
import styled from 'styled-components';
import { FONT_WEIGHTS, COLORS, TEXT_SIZES } from '../../../utils/constants';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import Button from '../../common/Button';
import { SlotDto, UpdateSlotType } from '../../../types/types';
import { utcToLocalDate } from '../../../utils/dateUtils';
import { updateSlotsRequest } from '../../../api/requests/slot';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';

interface UpdateSlotModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  courtName: string;
  courtId: number;
  selectedDate?: Dayjs;
  slotData: SlotDto;
}

const UpdateSlotModal: React.FC<UpdateSlotModalProps> = ({
  visible,
  onClose,
  onSubmit,
  courtName,
  courtId,
  selectedDate,
  slotData,
}) => {
  const [form] = Form.useForm();
  const snackbar = useSnackbar();

  const startTime = useMemo(
    () => utcToLocalDate(slotData.startTime),
    [slotData.startTime],
  );
  const endTime = useMemo(
    () => utcToLocalDate(slotData.endTime),
    [slotData.endTime],
  );
  useEffect(() => {
    if (visible && startTime && endTime) {
      form.setFieldsValue({
        startTime: dayjs(startTime),
        endTime: dayjs(endTime),
      });
    }
  }, [visible, startTime, endTime, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (
        values &&
        (!dayjs(startTime).isSame(values.startTime) ||
          !dayjs(endTime).isSame(values.endTime))
      ) {
        try {
          const data: UpdateSlotType = {
            courtId: courtId,
            startTime: values.startTime,
            endTime: values.endTime,
            slotId: slotData?.slotId,
          };
          const response = await updateSlotsRequest(data);
          if (response?.isSuccess) {
            form.resetFields();
            onSubmit();
            snackbar.success('Slot updated successfully');
          } else {
            snackbar.error(response?.error);
          }
        } catch (error: any) {
          snackbar.error(error?.Error || 'Error updating slot');
        }
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <UpdateModal
      title="Update Game Slot"
      open={visible}
      onCancel={onClose}
      minHeight={'auto'}
      footer={
        <>
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button key="submit" type="primary" onClick={handleSubmit}>
            Update Slot
          </Button>
        </>
      }
    >
      <HeaderWrapper>
        <DateText>Date: {selectedDate?.format('ddd, MMM D')}</DateText>
        <SubText>Updating slot for customer bookings</SubText>
      </HeaderWrapper>
      <BodyWrapper>
        <CourtText>{courtName}</CourtText>
        <FormWrapper>
          <Row gutter={16} justify={'start'}>
            <Col span={12}>
              <MandatorySymbol>* </MandatorySymbol>Start Time
            </Col>
            <Col span={12}>
              <MandatorySymbol>* </MandatorySymbol>End Time
            </Col>
          </Row>
          <Form form={form} layout="vertical">
            <SlotPairWrapper>
              <StyledFormItem
                name="startTime"
                rules={[
                  {
                    required: true,
                    message: 'Please select start time',
                  },
                ]}
              >
                <TimePicker
                  format="hh:mm A"
                  style={{ width: '100%' }}
                  use12Hours
                  size="large"
                />
              </StyledFormItem>
              <StyledFormItem
                name="endTime"
                rules={[
                  {
                    required: true,
                    message: 'Please select end time',
                  },
                ]}
              >
                <TimePicker
                  format="hh:mm A"
                  style={{ width: '100%' }}
                  use12Hours
                  size="large"
                />
              </StyledFormItem>
            </SlotPairWrapper>
          </Form>
        </FormWrapper>
      </BodyWrapper>
    </UpdateModal>
  );
};

const UpdateModal = styled(Modal)`
  .ant-modal-body {
    padding: 0px;
  }
`;

const HeaderWrapper = styled(Col)`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.grey.light};
  height: 64px;
  padding: 12px 24px;
`;

const BodyWrapper = styled(Col)`
  padding: 12px 24px 24px 24px;
`;

const DateText = styled(Typography.Text)`
  font-size: ${TEXT_SIZES.SM};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const SubText = styled(Typography.Text)`
  color: ${COLORS.grey.secondary};
`;

const CourtText = styled(Typography.Text)`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const FormWrapper = styled.div`
  margin-top: 12px;
`;

const SlotPairWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 8px 0px;
`;

const MandatorySymbol = styled.span`
  color: red;
`;

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  flex: 1;
  margin-bottom: 0;
`;

export default UpdateSlotModal;
